import React, { useState, useEffect, useCallback } from "react";
import { supabase } from "../supabase";

export default function ThankYou() {
  const [donations, setDonations] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    const [donRes, playerRes] = await Promise.all([
      supabase.from("donations").select("*").order("created_at", { ascending: false }),
      supabase.from("players").select("id, name"),
    ]);
    if (donRes.data) setDonations(donRes.data);
    if (playerRes.data) setPlayers(playerRes.data);
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const playerMap = {};
  players.forEach((p) => {
    playerMap[p.id] = p.name;
  });

  const generateMessage = (d) => {
    const donorName = d.is_anonymous ? "Supporter" : d.donor_name;
    const playerName = d.player_id ? playerMap[d.player_id] : "the Buchtel Griffins Baseball Program";
    const forText = d.player_id
      ? `supporting ${playerName}'s season`
      : "supporting the Buchtel Griffins Baseball Program";

    return `Dear ${donorName},

Thank you for your generous $${Number(d.amount)} donation ${forText}. Your support makes a real difference — helping our kids compete, grow, and dream.

Every dollar goes directly toward equipment, tournament fees, and coaching for our players. We truly appreciate your investment in these young athletes and our community.

With gratitude,
Coach
Buchtel Griffins Baseball Program Baseball`;
  };

  const copyMessage = () => {
    if (!selected) return;
    navigator.clipboard.writeText(generateMessage(selected)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-navy/40">Loading...</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
      <h1 className="font-display text-2xl font-bold text-navy mb-6">
        Thank-You Messages
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Donor picker */}
        <div className="bg-white rounded-xl border border-warm-gray overflow-hidden">
          <div className="px-5 py-3 border-b border-warm-gray">
            <h2 className="font-semibold text-navy text-sm">Select a Donor</h2>
          </div>
          <div className="max-h-[500px] overflow-y-auto divide-y divide-warm-gray">
            {donations.map((d) => (
              <button
                key={d.id}
                onClick={() => { setSelected(d); setCopied(false); }}
                className={`w-full text-left px-5 py-3 flex items-center gap-3 transition-colors ${
                  selected?.id === d.id
                    ? "bg-black/5"
                    : "hover:bg-cream/50"
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center shrink-0">
                  <span className="text-navy/60 text-xs font-bold">
                    {d.is_anonymous ? "?" : (d.donor_name || "?").charAt(0)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-navy truncate">
                    {d.is_anonymous ? "Anonymous" : d.donor_name}
                  </p>
                  <p className="text-xs text-navy/40">
                    ${Number(d.amount)} &middot;{" "}
                    {d.player_id
                      ? `for ${playerMap[d.player_id] || "Player"}`
                      : "Team donation"}
                  </p>
                </div>
              </button>
            ))}
            {donations.length === 0 && (
              <div className="px-5 py-8 text-center text-navy/40 text-sm">
                No donations yet.
              </div>
            )}
          </div>
        </div>

        {/* Message preview */}
        <div>
          {selected ? (
            <div className="bg-white rounded-xl border border-warm-gray p-5">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-semibold text-navy text-sm">
                  Message Preview
                </h2>
                <span className="text-xs text-navy/40">
                  To: {selected.donor_email}
                </span>
              </div>
              <div className="bg-cream rounded-lg p-4 mb-4">
                <pre className="text-sm text-navy whitespace-pre-wrap font-body leading-relaxed">
                  {generateMessage(selected)}
                </pre>
              </div>
              <button
                onClick={copyMessage}
                className="w-full bg-navy text-white font-semibold text-sm py-3 rounded-lg hover:bg-navy-light transition-colors"
              >
                {copied ? "Copied to Clipboard!" : "Copy Message"}
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-warm-gray p-8 flex items-center justify-center text-center">
              <div>
                <svg
                  className="w-12 h-12 text-navy/20 mx-auto mb-3"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
                  />
                </svg>
                <p className="text-navy/40 text-sm">
                  Select a donor to generate a thank-you message
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
